'use client';

import type { Voice } from 'ml-text-service-client';
import Image from 'next/image';
import { type JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text, getAccessibleFocusStyles } from '@videoblocks/design-system';

import { useVoiceCard } from '../_hooks/useVoiceCard';
import { PlayButton } from './PlayButton';
import { formatDisplayText } from './utils';

export interface ContributorVoiceCardProps {
    voice: Voice;
    imageUrl: string;
    imageAltText: string;
    setSelectedVoice: (voiceId: Voice['voice_id']) => void;
    selectedVoice: Voice['voice_id'] | null;
    playingVoiceId: Voice['voice_id'] | null;
    setPlayingVoiceId: (voiceId: Voice['voice_id'] | null) => void;
}

export const ContributorVoiceCard = ({
    voice,
    imageUrl,
    imageAltText,
    selectedVoice,
    setSelectedVoice,
    playingVoiceId,
    setPlayingVoiceId,
}: ContributorVoiceCardProps): JSX.Element => {
    const { name, description, descriptive, use_case, voice_id, preview_url } = voice;

    const { isSelected, isPlaying, showPlayControls, setIsHovered, handleCardClick, handlePlayPause } = useVoiceCard({
        voiceId: voice_id,
        previewUrl: preview_url,
        selectedVoice,
        playingVoiceId,
        setSelectedVoice,
        setPlayingVoiceId,
    });

    const formattedUseCase = formatDisplayText(use_case);
    const formattedDescriptiveText = [formattedUseCase, descriptive].filter(Boolean).join(', ') || null;

    return (
        <div
            role="button"
            tabIndex={0}
            aria-label={`Select voice ${name}`}
            aria-pressed={isSelected}
            data-testid="contributor-voice-card"
            className={twMerge(
                'bg-[#010101] min-h-[308px] w-full flex flex-col items-center justify-end rounded-lg border-neutral-dark-300 border py-7 relative hover:cursor-pointer',
                isSelected && 'ring-yellow-500 ring-2 border-transparent',
                getAccessibleFocusStyles(),
            )}
            onClick={handleCardClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onKeyDown={(event) => {
                if (event.target !== event.currentTarget || event.repeat) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleCardClick();
                }
            }}
        >
            <PlayButton
                className={twMerge(
                    'absolute top-4 left-4 z-10 focus-visible:opacity-100',
                    showPlayControls ? 'opacity-100' : 'opacity-0',
                )}
                previewUrl={voice.preview_url}
                isPlaying={isPlaying}
                handlePlayPause={handlePlayPause}
            />
            <div className="w-full h-full absolute top-0 left-0 z-0">
                <Image
                    src={imageUrl}
                    alt={imageAltText}
                    sizes={`(max-width: 672px) 350px, 266px`}
                    fill
                    className="object-contain pointer-events-none"
                />
            </div>
            <div className="flex flex-col relative z-10 items-center w-full max-w-[191px] gap-y-1">
                <div className="px-4 backdrop-blur-xs rounded-capsule border border-[rgba(255,255,255,0.12)]">
                    <Text fontSize="sm" fontWeight="semibold" className="text-center">
                        {name}
                    </Text>
                </div>
                {formattedDescriptiveText && (
                    <Text fontSize="sm" className="text-center text-yellow-600">
                        {formattedDescriptiveText}
                    </Text>
                )}
                <Text fontSize="sm" fontWeight="regular" className="text-center text-white">
                    English
                </Text>
                <Text fontSize="xs" className="text-center text-gray-500" fontWeight="regular">
                    {description}
                </Text>
            </div>
        </div>
    );
};
